import {Action, Module, Mutation} from 'vuex-module-decorators'
import {MdbsCore} from "@/api/tsclient";
import store, {
    alertModule,
    authModule,
    dataMigrationModule,
    languageModule, queryModule,
    rolesModule,
    studiesModule,
    studyDefinitionAdminModule,
    studyDefinitionModule,
    studyModule
} from "@/store";
import BaseModule from "@/store/modules/BaseModule";
// eslint-disable-next-line
import RightDto = MdbsCore.RightDto;
// eslint-disable-next-line
import UserDto = MdbsCore.UserDto;
import UserPasswordChangeForCurrentUserDto = MdbsCore.UserPasswordChangeForCurrentUserDto;
import Rights = MdbsCore.Rights;
import RoleDto = MdbsCore.RoleDto;

const userClient = new MdbsCore.UserClient();
const rightsClient = new MdbsCore.RightClient();

const SUPER_RIGHT = "SUPER_RIGHT";

@Module({namespaced: true, name: "currentUser", store: store})
export default class CurrentUserModule extends BaseModule {
    _user: UserDto | null = null;
    _role: RoleDto | null = null;
    rights: Array<RightDto> = [];

    get hasRight() {
        const that = this;
        return function (right: string | Rights) {
            const rightAsString = (typeof right === "string" ? right : Rights[right as number]);
            return that.rights.some(r => r.identifier == rightAsString || r.identifier == SUPER_RIGHT);
        }
    }

    get user() {
        return this._user;
    }

    get isNtdUser() {
        return this._user?.tags?.ntd?.isNtd ?? false;
    }

    get roleIdentifier(): string {
        const roleIdentifier = this._role?.identifier;
        if (!roleIdentifier) {
            throw new Error("current user role not loaded");
        }

        return roleIdentifier;
    }

    @Mutation
    setUser(user: UserDto) {
        this._user = user
    }

    @Mutation
    private setRole(role: RoleDto | null) {
        this._role = role;
    }

    @Mutation
    setRights(rights: Array<RightDto>) {
        this.rights.updateValues(rights)
    }

    @Mutation
    reset() {
        this._user = null;
        this.rights = [];
    }

    @Action({rawError: true})
    async load() {
        await this.execute(
            async () => {
                if (authModule.loggedIn) {
                    const currentUser = await userClient.getCurrentUser();

                    this.setUser(currentUser);
                    this.setRole(await rolesModule.getRoleById(currentUser.role?.id!));
                    this.setRights(await rightsClient.getAllRightsForCurrentUser());
                    await languageModule.loadAll();
                    await studiesModule.loadAll(); // TODO exchange with loadMy base on rights
                    // HACKY load any study that is the first
                    if (studiesModule.studies.length > 0){
                        await studyModule.loadById(studiesModule.studies[0].id)
                        await studyDefinitionModule.loadById(studiesModule.studies[0].studyDefinitionId ?? "")

                        queryModule.reset();
                    }
                } else {
                    this.reset()
                    languageModule.reset();
                    studiesModule.reset();
                    queryModule.reset();
                    // Covers the expired-token path, which never passes through authModule.logout().
                    dataMigrationModule.reset();
                    studyDefinitionAdminModule.reset();
                }
            });
    }

    @Action({rawError: true})
    async loadIfEmpty() {
        if (this._user != null)
            return;
        await this.load();
    }

    @Action({rawError: true})
    async changePassword(payload: { oldPassword: string, newPassword: string }) {
        this.startLoading();
        try {
            const currentUserDto = await userClient.getCurrentUser();
            const dto = new UserPasswordChangeForCurrentUserDto({
                id: currentUserDto.id ?? "",
                oldPasswordString: payload.oldPassword,
                newPasswordString: payload.newPassword
            })
            await userClient.updatePasswordForCurrentUser(dto);
            await this.load();
        } catch (e) {
            await alertModule.handleException(e);
            return false;
        } finally {
            this.stopLoading();
        }

        return true;
    }
}
